import { useCallback } from 'react';
import { useSelector } from "react-redux"; 
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import RECICLED from "components/photos/purchaseDelivery/recicled.png";
import { useGeneratePdfOnce } from "pages/hook/useGeneratePdfOnce.js";

const ReportPurchaseSendEmail = ({ formPurchaseSendEmail, setPdfBlob }) => {
  const headerPrinterPurchase = useSelector(
    (state) => state.loginUser.defaultticketsformat.headerPrinterPurchase
  );

  // --------------------------
  // HELPERS
  // --------------------------
  const formatNumber = useCallback(
    (num) =>
      num.toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const formatNumberEuro = useCallback(
    (num) =>
      `${num.toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} €`,
    []
  );

  const formatNumberTN = useCallback(
    (num) =>
      `${num.toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} Tn`,
    []
  );

  const isValidPngDataURL = useCallback((value) => {
    if (typeof value !== "string") return false;
    const trimmed = value.trim();
    if (trimmed === "") return false;
    if (trimmed.includes("static/media")) return true;
    if (!trimmed.startsWith("data:image/png;base64,")) return false;
    return trimmed.length > 200;
  }, []);

  const groupTaxes = useCallback((lines) => {
    const taxGroups = [];
    lines.forEach((line) => {
      const { tax, amount, estimatedprice, discount, taxincluded } = line;
      const taxableAmount = amount * estimatedprice * (1 - discount / 100);

      if (tax !== 0 && !taxincluded && taxableAmount !== 0) {
        const taxAmount = taxableAmount * (tax / 100);
        const existing = taxGroups.find((g) => g.taxRate === tax);
        existing
          ? (existing.taxAmount += taxAmount)
          : taxGroups.push({ taxRate: tax, taxAmount });
      }
    });
    return taxGroups;
  }, []);

  const getDateFormated = useCallback((getDate) => {
    const meses = [
      'Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio',
      'Agosto','Septiembre','Octubre','Noviembre','Diciembre'
    ];
    const hoy = getDate ? new Date(getDate) : new Date();
    return `${hoy.getDate()} de ${meses[hoy.getMonth()]} de ${hoy.getFullYear()}`;
  }, []);

  // --------------------------
  // generatePDF
  // --------------------------
  const generatePDF = useCallback(() => {
    if (!formPurchaseSendEmail) return;

    const doc = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: [210, 297],
    });

    const lines = formPurchaseSendEmail.linespurchasedelivery || [];

    if (lines.length > 0) {
      const taxGroups = groupTaxes(lines);
      let numLinesPerPurchase = 15;
      let totalPurchase = 0;
      let totalTax = 0;
 
      let numPages = Math.ceil(formPurchaseSendEmail.linespurchasedelivery.length / numLinesPerPurchase);
      if (numPages===0){
        numPages=1;
      } 
      for (let j = 0; j < 1; j++) { 
        let yOffset = 12;

        doc.addImage(RECICLED, 'PNG', 37, 4, 5, 5);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text("ALBARÁN", 150, yOffset - 4, 'left');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(14);

        const formatted_id =
          formPurchaseSendEmail.purchasenote.toString().padStart(8, "0");
        doc.text(`${formatted_id}`, 150, yOffset + 2, 'left');

        // HEADER impreso desde Redux
        if (headerPrinterPurchase) {
          let counter = 0;
          headerPrinterPurchase.split("\n").forEach((line) => {
            doc.setFont('helvetica', counter === 0 ? 'bold' : 'normal');
            doc.setFontSize(counter === 0 ? 8 : 7);
            doc.text(line, 40, yOffset, 'center');
            yOffset += 2.5;
            counter++;
          });
        }
        // FECHA
        yOffset = 38;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);

        const newDate =
          formPurchaseSendEmail.datepurchase &&
          getDateFormated(formPurchaseSendEmail.datepurchase);

        doc.text(`${newDate}`, 195, yOffset - 8, 'right');
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text("He recibido de D.", 15, yOffset, 'left');
        doc.text("Camión nº ", 15, yOffset+5, 'left');
        doc.text("GRNP N.º ", 77, yOffset+5, 'left');
        doc.text("Obra ", 15, yOffset+10, 'left');
        doc.text("Dirección ", 15, yOffset+15, 'left');
        doc.text("E-mail ", 15, yOffset+20, 'left');
        
        // Establecer el estilo de la línea punteada
        doc.setLineDash([0.2, 0.2], 0); // [2, 2] define el patrón de los puntos: 2px línea, 2px espacio
        // Dibujar la línea punteada
        doc.line(44, yOffset, 195, yOffset); // Dibuja una línea punteada desde (10, 20) hasta (200, 20)
        doc.line(34, yOffset+5, 75, yOffset+5); // Dibuja una línea punteada desde (10, 20) hasta (200, 20)
        doc.line(95, yOffset+5, 195, yOffset+5); // Dibuja una línea punteada desde (10, 20) hasta (200, 20)
        doc.line(25, yOffset+10, 195, yOffset+10); // Dibuja una línea punteada desde (10, 20) hasta (200, 20)
        doc.line(32, yOffset+15, 195, yOffset+15); // Dibuja una línea punteada desde (10, 20) hasta (200, 20)
        doc.line(26, yOffset+20, 195, yOffset+20); // Dibuja una línea punteada desde (10, 20) hasta (200, 20)
        doc.text(`${formPurchaseSendEmail.ori_tras_tradename.substr(0, 78)}`, 44, yOffset-1, 'left');
        doc.text(`${formPurchaseSendEmail.vehiclestore_licenseplate.substr(0, 20)}`, 34, yOffset+4, 'left');
        doc.text(`${formPurchaseSendEmail.rel_trans_grnp.substr(0, 30)}`, 95, yOffset+4, 'left');
        doc.text(`${formPurchaseSendEmail.ori_tras_workplace.substr(0, 78)}`, 25, yOffset+9, 'left');
        doc.text(`${formPurchaseSendEmail.ori_tras_street.substr(0, 78)}`, 32, yOffset+14, 'left');
        doc.text(`${formPurchaseSendEmail.ori_tras_email.substr(0, 78)}`, 26, yOffset+19, 'left');
        doc.setLineDash([]);
        doc.setFillColor('#FFFFFF');
        doc.setLineWidth(0.3);  
        doc.rect(15, 60, 27, 6, 'FD');  
        doc.rect(42, 60, 120, 6, 'FD'); 
        doc.rect(155, 60, 20, 6, 'FD');  
        doc.rect(175, 60, 20, 6, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6);
        doc.text("CANTIDAD/TONELADAS", 16, 64, 'left');
        doc.setFontSize(10);
        doc.text("CONCEPTO", 98,64, 'center');
        doc.text("PRECIO", 165, 64, 'center');
        doc.text("IMPORTE",185, 64, 'center');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        let startIndex = j * numLinesPerPurchase; // Índice inicial para esta página
        let endIndex = Math.min(startIndex + numLinesPerPurchase, formPurchaseSendEmail.linespurchasedelivery.length); // Índice final para esta página (máximo 10 líneas de RESIDUOS elementos)
        let addyOffset=0;
        yOffset=68;
        for (let i = startIndex; i < endIndex; i++) {
          if (formPurchaseSendEmail.linespurchasedelivery[i].article_namees.length>170){
            addyOffset=12;
          }else{
            if (formPurchaseSendEmail.linespurchasedelivery[i].article_namees.length>85){
              addyOffset=8;
            }else{
              addyOffset=4;
            } 
          } 
          doc.setFillColor('#FFFFFF');
          doc.setLineWidth(0.2);  
          doc.rect(15, yOffset-3, 27, addyOffset, 'FD');  
          doc.rect(42, yOffset-3, 120, addyOffset, 'FD'); 
          doc.rect(155, yOffset-3, 20, addyOffset, 'FD');  
          doc.rect(175, yOffset-3, 20, addyOffset, 'FD');
          
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          if (formPurchaseSendEmail.linespurchasedelivery[i].ticketweight){
            doc.text(`${formatNumberTN(formPurchaseSendEmail.linespurchasedelivery[i].amount)}`, 41, yOffset, 'right');
          }else{
            doc.text(`${formatNumber(formPurchaseSendEmail.linespurchasedelivery[i].amount)}`, 41, yOffset, 'right');
          }
          totalPurchase+=formPurchaseSendEmail.linespurchasedelivery[i].amount*formPurchaseSendEmail.linespurchasedelivery[i].estimatedprice*(1-(formPurchaseSendEmail.linespurchasedelivery[i].discount/100));
          if (!formPurchaseSendEmail.linespurchasedelivery[i].taxincluded){
            totalTax+=(formPurchaseSendEmail.linespurchasedelivery[i].amount*formPurchaseSendEmail.linespurchasedelivery[i].estimatedprice*(1-(formPurchaseSendEmail.linespurchasedelivery[i].discount/100)))*(formPurchaseSendEmail.linespurchasedelivery[i].tax/100);
          }
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          if (formPurchaseSendEmail.linespurchasedelivery[i].article_namees.length>160){
            doc.text(`${formPurchaseSendEmail.linespurchasedelivery[i].article_namees.substr(0, 85)}`, 43, yOffset, 'left');
            doc.text(`${formPurchaseSendEmail.linespurchasedelivery[i].article_namees.substr(85,85)}`, 43, yOffset+4, 'left'); 
            doc.text(`${formPurchaseSendEmail.linespurchasedelivery[i].article_namees.substr(170)}`, 43, yOffset+8, 'left'); 
            doc.text(`${formatNumberEuro(formPurchaseSendEmail.linespurchasedelivery[i].estimatedprice*(1-(formPurchaseSendEmail.linespurchasedelivery[i].discount/100)))}`, 173, yOffset+8, 'right');
            doc.text(`${formatNumberEuro(formPurchaseSendEmail.linespurchasedelivery[i].amount*formPurchaseSendEmail.linespurchasedelivery[i].estimatedprice*(1-(formPurchaseSendEmail.linespurchasedelivery[i].discount/100)))}`, 193, yOffset+8, 'right');
          
          }else{
            if (formPurchaseSendEmail.linespurchasedelivery[i].article_namees.length>85){
              doc.text(`${formPurchaseSendEmail.linespurchasedelivery[i].article_namees.substr(0, 85)}`, 43, yOffset, 'left');
              doc.text(`${formPurchaseSendEmail.linespurchasedelivery[i].article_namees.substr(85)}`, 43, yOffset+4, 'left'); 
              doc.text(`${formatNumberEuro(formPurchaseSendEmail.linespurchasedelivery[i].estimatedprice*(1-(formPurchaseSendEmail.linespurchasedelivery[i].discount/100)))}`, 173, yOffset+4, 'right');
              doc.text(`${formatNumberEuro(formPurchaseSendEmail.linespurchasedelivery[i].amount*formPurchaseSendEmail.linespurchasedelivery[i].estimatedprice*(1-(formPurchaseSendEmail.linespurchasedelivery[i].discount/100)))}`, 193, yOffset+4, 'right');
            
            }else{
              doc.text(`${formPurchaseSendEmail.linespurchasedelivery[i].article_namees.substr(0, 85)}`, 43, yOffset, 'left'); 
              doc.text(`${formatNumberEuro(formPurchaseSendEmail.linespurchasedelivery[i].estimatedprice*(1-(formPurchaseSendEmail.linespurchasedelivery[i].discount/100)))}`, 173, yOffset, 'right');
              doc.text(`${formatNumberEuro(formPurchaseSendEmail.linespurchasedelivery[i].amount*formPurchaseSendEmail.linespurchasedelivery[i].estimatedprice*(1-(formPurchaseSendEmail.linespurchasedelivery[i].discount/100)))}`, 193, yOffset, 'right');
            } 
          } 
        
          yOffset+=addyOffset;  
        } 
        doc.setFillColor('#FFFFFF');
        doc.setLineWidth(0.3);  
        doc.line(15, yOffset-3, 195, yOffset-3);
        doc.rect(175, yOffset-3, 20, 4, 'FD');
        yOffset+=4;
        doc.rect(175, yOffset-3, 20, 4, 'FD');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text("SUBTOTAL", 173, yOffset-4, 'right');
        doc.text(`${formatNumberEuro(totalPurchase)}`, 193, yOffset-4, 'right');
        yOffset+=4; 
        if (taxGroups && taxGroups.length>0){
          taxGroups.forEach((group) => {
            doc.setFillColor('#FFFFFF');
            doc.setLineWidth(0.3);  
            doc.rect(175, yOffset-3, 20, 4, 'FD');
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text(`IGIC ${group.taxRate}%:`, 173, yOffset, 'right'); 
            doc.text(`${formatNumberEuro(group.taxAmount)}`, 193, yOffset, 'right');
            yOffset+=4;
            
          }); 
        }else{
          doc.setFillColor('#FFFFFF');
          doc.setLineWidth(0.3);  
          doc.rect(175, yOffset-3, 20, 4, 'FD');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.text("IGIC", 173, yOffset, 'right');
          yOffset+=4;
        }
        doc.setFillColor('#FFFFFF');
        doc.setLineWidth(0.3);  
        doc.rect(175, yOffset-3, 20, 4, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text("TOTAL", 173, yOffset, 'right');
        let paymentData="FORMA DE PAGO: "+formPurchaseSendEmail.payment_namees;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        //doc.text(`${formatNumberEuro(totalTax)}`, 193, yOffset, 'right');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(`${formatNumberEuro(totalPurchase+totalTax)}`, 193, yOffset, 'right');
        doc.text(`${paymentData}`, 195,yOffset+8, 'right');
        if (formPurchaseSendEmail.ownnumberpurchase && formPurchaseSendEmail.ownnumberpurchase.trim()!==null && formPurchaseSendEmail.ownnumberpurchase.trim()!=="") {
          let referenceData="Referencia Cliente: "+formPurchaseSendEmail.ownnumberpurchase.trim();
          doc.text(`${referenceData}`, 195,yOffset+12, 'right');
        }
        yOffset=115
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text("DNI nº", 82, yOffset+16, 'left');
        doc.setLineDash([0.2, 0.2], 0); // [2, 2] define el patrón de los puntos: 2px línea, 2px espacio
        // Dibujar la línea punteada
        doc.line(92, yOffset+16, 120, yOffset+16); // Dibuja una línea punteada desde (10, 20) hasta (200, 20)
        doc.setLineDash([]);
        if (formPurchaseSendEmail.signature_dispatched_by!==undefined && formPurchaseSendEmail.signature_dispatched_by!=="" && isValidPngDataURL(formPurchaseSendEmail.signature_dispatched_by)){
          try {
            doc.addImage(`${formPurchaseSendEmail.signature_dispatched_by}`, 'PNG', 17, yOffset+2, 20, 20);
          } catch (e) { 
          }
        } 
        if (formPurchaseSendEmail.signature_client!==undefined && formPurchaseSendEmail.signature_client!=="" && isValidPngDataURL(formPurchaseSendEmail.signature_client)){
          try {
                doc.addImage(`${formPurchaseSendEmail.signature_client}`, 'PNG', 98, yOffset+5, 8, 8);
          } catch (e) {
          }
        } 
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text("Despachado por", 18, yOffset+3, 'left');
        doc.text("Conforme Cliente", 100, yOffset+3, 'center');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text("*Este documento no tiene valor como  'Certificado Acreditativo de Gestión de Residuos de la Construcción'  según el art. 7.c. del Real Decreto 105/2008", 12, yOffset+24, 'left');  
      } 
    }
    const pdfBlob = doc.output("blob");
    if (pdfBlob) setPdfBlob(pdfBlob);
  }, [
    formPurchaseSendEmail,
    setPdfBlob,
    headerPrinterPurchase,
    formatNumber,
    formatNumberEuro,
    formatNumberTN,
    getDateFormated,
    isValidPngDataURL,
    groupTaxes,
  ]);

  // --------------------------
  // CONDICIÓN PARA GENERAR
  // --------------------------
  const ready =
    !!formPurchaseSendEmail &&
    !!formPurchaseSendEmail.id &&
    formPurchaseSendEmail.id !== 0 &&
    formPurchaseSendEmail.id !== "0";

  // --------------------------
  // EJECUTAR SOLO UNA VEZ
  // --------------------------
  useGeneratePdfOnce(ready, generatePDF);

  return null;
};

export default ReportPurchaseSendEmail;